/**
 * AppLayout — Authenticated layout shell
 * 
 * Wraps all protected pages with Sidebar + Topbar.
 * <Outlet /> renders the active route's page component.
 * Replaces the old manual page-switching logic in App().
 */

import { memo, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Sidebar, Topbar } from '../components/Sidebar';
import { Toast } from '../components/shared';

// Map URL path segments to display labels (used by Topbar breadcrumb)
export const PATH_LABELS = {
  dashboard:    'Dashboard',
  ai:           'AI Intelligence',
  team:         'Team',
  users:        'Users',
  analytics:    'Member Analytics',
  leads:        'Leads',
  edit:         'Edit Profile',
  subteams:     'Subteams',
  new:          'Create User',
  requests:     'Requests',
  map:          'Maps',
  board:        'LeadsBoard',
  insights:     'Analytics',
  campaigns:    'Campaigns',
  email:        'Email Campaign',
  notification: 'Notification Campaign',
  nfc:          'NFC Card Builder',
  automations:  'Smart Automations',
  activity:     'Activity Feed',
  notifications:'Notification Center',
  signature:    'Email Signature',
  integrations: 'Integrations',
  billing:      'Billing & Plans',
  settings:     'Settings',
  support:      'Support',
  'help-center':'Help Center',
  contact:      'Contact Support',
  'live-chat':  'Live Chat',
  ticket:       'Submit a Ticket',
  tutorials:    'Video Tutorials',
  faq:          'FAQ',
  team:         'Team Settings',
  security:     'Security',
  api:          'API & Developers',
  privacy:      'Data & Privacy',
};

const AppLayout = memo(() => {
  const { user, logout, toast, dismissToast } = useApp();
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1] || 'dashboard';
    const label = PATH_LABELS[last] || PATH_LABELS[segments[0]] || 'Dashboard';
    document.title = `GOtaps - ${label}`;
  }, [location.pathname]);

  return (
    <>
      <div className="mesh" />
      <div className="layout">
        <Sidebar
          location={location}
          navigate={navigate}
          user={user}
          onLogout={logout}
        />
        <main className="main">
          <Topbar
            location={location}
            navigate={navigate}
            pathLabels={PATH_LABELS}
            user={user}
            onLogout={logout}
          />
          <div key={location.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
      {toast && (
        <Toast
          key={toast.msg + Date.now()}
          msg={toast.msg}
          type={toast.type}
          onDone={dismissToast}
        />
      )}
    </>
  );
});

export default AppLayout;
